import { translationService } from "../../../../services/translation/TranslationService";
import { ModalContentStrategy } from "./ModalContentStrategy";
import { ModalElement, MessageContent } from "./ModalContentContext";
import { SubscriptionPlan } from "../../../../services/organization-subscription-plans/SubscriptionPlan";
import { getLocalPrice } from "../../../../helpers/MathHelpers";

export class DowngradePlanContentBuilder implements ModalContentStrategy {
  private currentSeatsAmount: number;
  private newSeatsAmount: number;

  private currentExtraStorage: number;
  private updatedExtraStorage: number;
  private extraStoraceSpaceDifference: number;

  private currentExtraVAAmount: number;
  private extraVAAmount: number;
  private extraVAPackageDifference: number;

  private quarterlyPayment: string;
  private startDate: string;
  private currentPlanName: string;
  private newPlanName: string;

  private updates: ModalElement[] = [];
  private invoice: ModalElement[] = [];
  private info: ModalElement[] = [];

  constructor(
    seatsAmount: number,
    extraStorageAmount: number,
    extraVAAmount: number,
    quarterlyPayment: number,
    startDate: string,
    newPlanName: string,
    activeSubscription: SubscriptionPlan
  ) {
    this.currentPlanName = activeSubscription.name;
    this.newPlanName = newPlanName;

    this.currentSeatsAmount = activeSubscription.activeSubscriptionPackageAmount || 0;
    this.newSeatsAmount = seatsAmount;

    this.currentExtraStorage =
      activeSubscription.extraStoragePackage?.activeSubscriptionPackageAmount || 0;
    this.updatedExtraStorage = extraStorageAmount;

    this.currentExtraVAAmount =
      activeSubscription.extraVACalls?.activeSubscriptionPackageAmount || 0;
    this.extraVAAmount = extraVAAmount;

    this.quarterlyPayment = getLocalPrice(quarterlyPayment);
    this.startDate = startDate;

    this.extraStoraceSpaceDifference = Math.abs(
      this.updatedExtraStorage - this.currentExtraStorage
    );
    this.extraVAPackageDifference = Math.abs(this.extraVAAmount - this.currentExtraVAAmount);
  }

  buildModalContent(): MessageContent {
    this.initializeLists();

    this.updateDowngradedPlanInfo();

    this.updatePaymentInfo();

    this.updateGeneralDowngradeInfo();

    this.endLists();

    return { updates: this.updates, invoice: this.invoice, info: this.info };
  }

  private initializeLists() {
    this.updates.push({
      displayText: translationService.translate(
        "SubscriptionPlans.card.update-plan.modal-confirmation-part-13",
        { nextPaymentDate: this.startDate }
      ),
      isStartList: true,
    });

    if (
      this.updatedExtraStorage > this.currentExtraStorage ||
      this.extraVAAmount > this.currentExtraVAAmount
    ) {
      this.invoice.push({
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-3",
          { nextPaymentDate: this.startDate }
        ),
        isStartList: true,
      });
    }
  }

  private updateDowngradedPlanInfo() {
    this.updatePlanNameMessage();

    if (this.currentSeatsAmount !== this.newSeatsAmount) this.updateSeatsMessage();

    if (this.currentExtraStorage !== this.updatedExtraStorage) this.updateExtraStorageMessage();

    if (this.currentExtraVAAmount !== this.extraVAAmount) this.updateExtraVAMessage();
  }

  private updatePlanNameMessage() {
    this.updates.push(
      {
        isStartItem: true,
      },
      {
        displayText: translationService.translate("general.from"),
      },
      {
        displayText: ` ${translationService.translate(`SubscriptionPlans.card.${this.currentPlanName}`)}`,
        shouldDisplayBolded: true,
      },
      {
        displayText: translationService.translate("general.to"),
      },
      {
        displayText: ` ${translationService.translate(`SubscriptionPlans.card.${this.newPlanName}`)}`,
        shouldDisplayBolded: true,
      },
      { isEndItem: true }
    );
  }

  private updateSeatsMessage() {
    this.updates.push(
      {
        isStartItem: true,
      },
      { displayText: translationService.translate("general.from") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-before"
        ),
        displayText: ` ${this.currentSeatsAmount}${translationService.translate(
          "SubscriptionPlans.card.seats-general"
        )}`,
        shouldDisplayBolded: true,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-after"
        ),
      },
      { displayText: translationService.translate("general.to") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-before"
        ),
        displayText: ` ${this.newSeatsAmount}${translationService.translate(
          "SubscriptionPlans.card.seats-general"
        )}`,
        shouldDisplayBolded: true,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-4-after"
        ),
      },
      {
        isEndItem: true,
      }
    );
  }

  private updateExtraStorageMessage() {
    this.updates.push(
      {
        isStartItem: true,
      },
      { displayText: translationService.translate("general.from") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-before"
        ),
        displayText: ` ${this.currentExtraStorage}TB`,
        shouldDisplayBolded: true,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-after"
        ),
      },
      { displayText: translationService.translate("general.to") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-before"
        ),
        displayText: ` ${this.updatedExtraStorage}TB`,
        shouldDisplayBolded: true,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-after"
        ),
      },
      {
        isEndItem: true,
      }
    );

    if (this.updatedExtraStorage > this.currentExtraStorage) {
      this.invoice.push(
        {
          isStartItem: true,
        },
        {
          displayText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-7"
          ),
        },
        {
          displayText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-before"
          ),
        },
        {
          displayText: ` ${this.extraStoraceSpaceDifference}TB`,
        },
        {
          displayText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-8-after"
          ),
        },
        {
          isEndItem: true,
        }
      );
    }
  }

  private updateExtraVAMessage() {
    this.updates.push(
      {
        isStartItem: true,
      },
      { displayText: translationService.translate("general.from") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-before"
        ),
        displayText: ` ${this.currentExtraVAAmount} ${translationService.translate(
          "SubscriptionPlans.card.extra-video-analysis-packages"
        )}`,
        shouldDisplayBolded: true,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-after"
        ),
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-10"
        ),
      },
      {
        displayText: ` ${this.currentExtraVAAmount * 50}`,
        shouldDisplayBolded: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-11"
        ),
      },
      { displayText: translationService.translate("general.to") },
      {
        beforeText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-before"
        ),
        displayText: ` ${this.extraVAAmount} ${translationService.translate(
          "SubscriptionPlans.card.extra-video-analysis-packages"
        )}`,
        shouldDisplayBolded: true,
        afterText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-9-after"
        ),
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-10"
        ),
      },
      {
        displayText: ` ${this.extraVAAmount * 50}`,
        shouldDisplayBolded: true,
      },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.update-plan.modal-confirmation-part-11"
        ),
      },
      {
        isEndItem: true,
      }
    );

    if (this.extraVAAmount > this.currentExtraVAAmount) {
      this.invoice.push(
        {
          isStartItem: true,
        },
        {
          displayText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-7"
          ),
        },
        {
          displayText: translationService.translate(
            "SubscriptionPlans.card.update-plan.modal-confirmation-part-12",
            {
              vaDifference: this.extraVAPackageDifference,
              totalVACalls: this.extraVAPackageDifference * 50,
            }
          ),
        },
        {
          isEndItem: true,
        }
      );
    }
  }

  private updatePaymentInfo() {
    this.info.push({
      beforeText: translationService.translate(
        "SubscriptionPlans.card.update-plan.modal-confirmation-part-2-before"
      ),
      displayText: ` ${this.quarterlyPayment}€`,
      shouldDisplayBolded: true,
      afterText: translationService.translate(
        "SubscriptionPlans.card.update-plan.modal-confirmation-part-2-after"
      ),
    });
  }

  private updateGeneralDowngradeInfo() {
    this.info.push(
      { isEmptyLine: true },
      { isEmptyLine: true },
      {
        displayText: translationService.translate(
          "SubscriptionPlans.card.downgrade-plan.modal-confirmation-part-1"
        ),
      }
    );
  }

  private endLists() {
    this.updates.push({
      isEndList: true,
    });

    if (
      this.updatedExtraStorage > this.currentExtraStorage ||
      this.extraVAAmount > this.currentExtraVAAmount
    ) {
      this.invoice.push({
        isEndList: true,
      });
    }
  }
}
